import type { SubscriptionPlan, SubscriptionPrice } from "@/types/subscription";

export function useSubscriptionApi() {
  const { t } = useFormatOptions();
  const subscriptionStore = useSubscriptionStore();
  const appStore = useAppStore();
  const { currentUser, currentOrganization, refreshCurrentUser } =
    useOrganizationApi();
  const { currentRouteContext, homeRoute, createSubscriptionPath } =
    useSubscriptionRoute();

  const changeSubscriptionMutate = useSChangeSubscription();
  const cancelSubscriptionMutate = useCancelSubscription();
  const createPaymentCheckoutSessionMutate = useCreateCheckoutSession();
  const createPaymentMethodUpdateCheckoutSessionMutate =
    useCreatePaymentMethodCheckoutSession();
  const getCheckoutSessionFetch = useGetCheckoutSession();
  const getPaymentMethodCheckoutSessionFetch =
    useGetPaymentMethodCheckoutSession();

  const userCanManageSubscription = computed(
    () => currentOrganization.value?.userCanManageSubscription,
  );

  const paymentInfoRequired = computed(
    () => currentOrganization.value?.paymentInfoRequired,
  );

  const availablePlans = computed(() => {
    const current = currentOrganization.value?.subscription ?? undefined;

    return enrichAvailablePlans(subscriptionStore.planList, current);
  });

  const currentSubscription = computed(() => {
    if (!currentOrganization.value?.subscription) return;
    const { subscription, ...organization } = currentOrganization.value;
    const plan = availablePlans.value?.find(
      (plan) => plan.slug === subscription.slug,
    );
    const price = plan?.prices.find(
      (price) => price.id === subscription.subscriptionId,
    );

    return {
      ...subscription,
      organization,
      plan,
      price,
    };
  });

  const isCurrentSubscriptionActive = computed(() => {
    return (
      currentSubscription.value?.isActive &&
      !currentSubscription.value?.isExpired
    );
  });

  const eligibleForTrial = computed(() => {
    // TODO: at the moment a user can only start trial if there is no current subscription
    if (!currentSubscription.value) return true;

    return false;
  });

  const isWizardInitialized = computed(
    () => subscriptionStore.isWizardInitialized,
  );

  const paymentCheckoutSession = computed(
    () => subscriptionStore.paymentCheckoutSession,
  );

  const paymentMethodUpdateCheckoutSession = computed(
    () => subscriptionStore.paymentMethodUpdateCheckoutSession,
  );

  const sourceSubscription = computed(() => {
    if (!subscriptionStore.sourceSubscription) return;
    const plan = availablePlans.value?.find(
      (plan) => plan.slug === subscriptionStore.sourceSubscription?.plan,
    );
    const price = plan?.prices.find(
      (price) => price.id === subscriptionStore.sourceSubscription?.priceId,
    );

    if (!plan || !price) {
      devConsole.warn("Failed to find source subscription plan or price", {
        sourceSubscription: subscriptionStore.sourceSubscription,
        plan,
        price,
      });
      return;
    }

    const isTrial =
      eligibleForTrial.value ||
      (price.id === currentSubscription.value?.price?.id &&
        !!currentSubscription.value?.isTrialPeriod);

    return { plan, price, isTrial };
  });

  const targetSubscription = computed(() => {
    if (!subscriptionStore.targetSubscription) return;
    const plan = availablePlans.value?.find(
      (plan) => plan.slug === subscriptionStore.targetSubscription?.plan,
    );
    const price = plan?.prices.find(
      (price) => price.id === subscriptionStore.targetSubscription?.priceId,
    );

    if (!plan || !price) {
      devConsole.warn("Failed to find target subscription plan or price", {
        targetSubscription: subscriptionStore.targetSubscription,
        plan,
        price,
      });
      return;
    }

    const isTrial =
      eligibleForTrial.value ||
      (price.id === currentSubscription.value?.price?.id &&
        !!currentSubscription.value?.isTrialPeriod);

    return { plan, price, isTrial };
  });

  const wizardSteps = computed(() =>
    subscriptionSteps.map((step) => ({
      ...step,
      disabled: isSubscriptionStepDisabled(step, {
        lastVisitedStepIndex: subscriptionStore.latestVisitedStepIndex,
        isFree: targetSubscription.value?.plan.isFree,
      }),
      name: `subscription-${step.id}`,
      url: createSubscriptionPath({ wizardStep: step }),
    })),
  );

  const currentStep = computed(() => {
    const routeName =
      currentRouteContext.value?.normalizedName?.toLowerCase() ?? "";

    return wizardSteps.value.findIndex(
      ({ name }) => routeName === name.toLowerCase(),
    );
  });

  const showLeaveConfirmationDialog = computed(
    () => subscriptionStore.unsavedChangesState.showLeaveConfirmation,
  );

  const checkoutMessageType = computed(() => {
    if (!currentUser.value) {
      return "createUserDescription" as const;
    }
    if (
      sourceSubscription.value?.price &&
      targetSubscription.value?.price?.id !== sourceSubscription.value.price.id
    ) {
      return "switchingSubscriptionDescription" as const;
    }

    if (
      targetSubscription.value?.isTrial &&
      targetSubscription.value.price?.trialPeriodDays
    ) {
      return "trialSubscriptionDescription" as const;
    }

    return "newSubscriptionDescription" as const;
  });

  const clearCheckoutSessions = () => {
    subscriptionStore.paymentCheckoutSession = undefined;
    subscriptionStore.paymentMethodUpdateCheckoutSession = undefined;
  };

  const createPaymentCheckoutSession = async (subscriptionId: number) => {
    const session = await createPaymentCheckoutSessionMutate.safeMutate({
      subscriptionId,
    });
    if (!session) {
      appStore.addError(
        t("cksetup.subscription.payment-step.failedToCreateCheckoutSession"),
      );
      return;
    }

    subscriptionStore.paymentCheckoutSession = session;

    return session;
  };

  const createPaymentMethodUpdateCheckoutSession = async () => {
    const session =
      await createPaymentMethodUpdateCheckoutSessionMutate.safeMutate();

    if (!session) {
      appStore.addError(
        t("cksetup.settings.payment-method.failedToCreateCheckoutSession"),
      );
      return;
    }

    subscriptionStore.paymentMethodUpdateCheckoutSession = session;
    return session;
  };

  const validatePaymentCheckoutSession = async (sessionId: string) => {
    const checkoutSession = await getCheckoutSessionFetch.safeFetch(sessionId);
    if (!checkoutSession) {
      devConsole.error(
        "Failed to retrieve checkout session with id",
        sessionId,
      );
      return { success: false };
    }

    const plan = availablePlans.value.find(
      ({ slug }) => slug === checkoutSession.subscriptionSlug,
    );

    const price = plan?.prices.find(
      ({ id }) => id === checkoutSession.subscriptionId,
    );

    if (!plan || !price) {
      devConsole.error("Failed to find plan for the checkout session.", {
        checkoutSession,
        plan,
        price,
      });

      return { success: false };
    }

    if (
      checkoutSession.status !== "complete" ||
      checkoutSession.paymentStatus === "unpaid"
    ) {
      appStore.addError(t("cksetup.subscription.payment-step.sessionError"));

      return {
        checkoutSession,
        success: false,
      };
    }

    subscriptionStore.reset();
    await refreshCurrentUser();

    appStore.addSuccessMessage(
      t("cksetup.subscription.subscribedSuccess", {
        name: t(plan.name),
      }),
    );

    return {
      checkoutSession,
      success: true,
    };
  };

  const validatePaymentMethodUpdateCheckoutSession = async (
    sessionId: string,
  ) => {
    const checkoutSession =
      await getPaymentMethodCheckoutSessionFetch.safeFetch(sessionId);
    if (!checkoutSession) {
      devConsole.error(
        "Failed to retrieve payment method checkout session with id",
        sessionId,
      );
      appStore.addError(t("cksetup.subscription.payment-step.sessionError"));
      return;
    }

    await refreshCurrentUser();

    appStore.addSuccessMessage(
      t("cksetup.settings.paymentMethodSuccessMessage"),
    );

    return checkoutSession;
  };

  async function changeSubscription(subscriptionId: number) {
    const success = await changeSubscriptionMutate.safeMutate(subscriptionId);

    if (success) {
      appStore.addSuccessMessage(
        t("cksetup.subscription.subscribedSuccess", {
          name: t(targetSubscription.value?.plan.name ?? ""),
        }),
      );
      subscriptionStore.unsavedChangesState.hasUnsavedChanges = false;
      await refreshCurrentUser();
      return success;
    }
  }

  function navigateToStep(
    step: SubscriptionStep | SubscriptionStepId | number,
  ) {
    subscriptionStore.latestVisitedStepIndex = currentStep.value;
    return navigateTo(createSubscriptionPath({ wizardStep: step }));
  }

  async function selectSubscription(
    plan: SubscriptionPlan,
    price: SubscriptionPrice,
  ) {
    if (!price.isActive) {
      return;
    }

    subscriptionStore.init({
      plan: plan.slug,
      priceId: price.id,
      sourceSlug: currentSubscription.value?.plan?.slug,
      sourcePriceId: currentSubscription.value?.price?.id,
    });

    if (!currentUser.value) {
      navigateToStep("signup");
      return;
    }

    if (currentSubscription.value?.isActive) {
      const success = await changeSubscription(price.id);
      if (!success) return;
    }

    navigateToStep("payment");
  }

  function cancelLeave() {
    subscriptionStore.unsavedChangesState.confirmationLeaveRoute = undefined;
    subscriptionStore.unsavedChangesState.showLeaveConfirmation = false;
  }

  function confirmLeave() {
    subscriptionStore.unsavedChangesState.hasUnsavedChanges = false;
    subscriptionStore.unsavedChangesState.showLeaveConfirmation = false;

    if (subscriptionStore.unsavedChangesState.confirmationLeaveRoute) {
      navigateTo(subscriptionStore.unsavedChangesState.confirmationLeaveRoute);
      subscriptionStore.unsavedChangesState.confirmationLeaveRoute = undefined;
    }
  }

  const cancelSubscription = async () => {
    const result = await cancelSubscriptionMutate.safeMutate();

    if (result) {
      await refreshCurrentUser();
      appStore.addSuccessMessage(
        t("cksetup.settings.subscription.cancelSubscriptionSuccessMessage"),
      );
    }
  };

  return {
    isWizardInitialized,
    userCanManageSubscription,
    paymentInfoRequired,
    checkoutMessageType,
    homeRoute,
    availablePlans,
    subscriptionStore,
    targetSubscription,
    sourceSubscription,
    currentSubscription,
    isCurrentSubscriptionActive,
    eligibleForTrial,
    wizardSteps,
    currentStep,
    showLeaveConfirmationDialog,
    paymentCheckoutSession,
    paymentMethodUpdateCheckoutSession,
    clearCheckoutSessions,
    createSubscriptionPath,
    navigateToStep,
    loadAvailablePlans: subscriptionStore.loadAvailablePlans,
    selectSubscription,
    createPaymentCheckoutSession,
    createPaymentMethodUpdateCheckoutSession,
    validatePaymentCheckoutSession,
    validatePaymentMethodUpdateCheckoutSession,
    cancelSubscription,
    cancelLeave,
    confirmLeave,
  };
}
